import { Plus, ShieldCheck, UserX } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { InviteUserDialog } from "@/features/users/components/InviteUserDialog";
import { useUpdateUser, useUsers } from "@/features/users/hooks/useUsers";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

const ALL_TEAMS = "__all__";

export function UsersListPage() {
  const { t } = useTranslation("users");
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [teamId, setTeamId] = useState<string>(ALL_TEAMS);
  const teamsQuery = useTeams();

  const filters = useMemo(
    () => ({
      q: q.trim() || undefined,
      is_active: showInactive ? undefined : true,
      team_id: teamId !== ALL_TEAMS ? teamId : undefined,
    }),
    [q, showInactive, teamId],
  );

  const { data, isLoading, isError, error, refetch } = useUsers(filters);
  const canInvite = useCan(Action.USER_INVITED);

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          <Button onClick={() => setInviteOpen(true)} disabled={!canInvite}>
            <Plus className="h-4 w-4" />
            {t("invite")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t("filters.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <Select value={teamId} onValueChange={setTeamId}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("filters.byTeam")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TEAMS}>{t("filters.allTeams")}</SelectItem>
            {teamsQuery.data?.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showInactive ? "secondary" : "outline"}
          onClick={() => setShowInactive((v) => !v)}
        >
          {showInactive ? t("filters.hideInactive") : t("filters.showInactive")}
        </Button>
      </div>

      {data && data.length === 0 ? (
        <EmptyState
          title={t("empty.title")}
          description={t("empty.description")}
        />
      ) : (
        <ul className="shadow-card divide-y divide-line rounded-2xl border border-line bg-surface">
          {data?.map((user) => (
            <UserRow key={user.id} user={user} />
          ))}
        </ul>
      )}

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

interface UserRowProps {
  user: {
    id: string;
    email: string;
    name: string;
    is_admin: boolean;
    is_active: boolean;
    pii_filter_enabled: boolean;
  };
}

function UserRow({ user }: UserRowProps) {
  const { t } = useTranslation("users");
  const updateUser = useUpdateUser(user.id);
  const canManage = useCan(Action.USER_UPDATED);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function toggleActive() {
    try {
      await updateUser.mutateAsync({ is_active: !user.is_active });
      toast.success(
        user.is_active ? t("toasts.deactivated") : t("toasts.reactivated"),
      );
      setConfirmOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-canvas/60">
      <div className="flex flex-col">
        <span className="font-medium text-ink-950">{user.name}</span>
        <span className="text-sm text-ink-500">{user.email}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {user.is_admin ? (
          <Badge variant="default">
            <ShieldCheck className="mr-1 h-3 w-3" />
            {t("badges.admin")}
          </Badge>
        ) : null}
        {user.pii_filter_enabled ? (
          <Badge variant="secondary">{t("badges.pii")}</Badge>
        ) : null}
        {!user.is_active ? (
          <Badge variant="destructive">
            <UserX className="mr-1 h-3 w-3" />
            {t("badges.inactive")}
          </Badge>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            if (user.is_active) {
              setConfirmOpen(true);
            } else {
              void toggleActive();
            }
          }}
          disabled={!canManage || updateUser.isPending}
        >
          {user.is_active ? t("actions.deactivate") : t("actions.activate")}
        </Button>
      </div>

      <ConfirmDestructiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("deactivate.title")}
        description={t("deactivate.description")}
        resourceName={user.name}
        resourceKind={t("deactivate.resourceKind")}
        confirmLabel={t("deactivate.confirm")}
        onConfirm={toggleActive}
        isPending={updateUser.isPending}
      />
    </li>
  );
}
